const ACCENT = "#0f766e";
const ACCENT_SOFT = "#ecfdf5";
const MUTED = "#64748b";
const LINE = "#e5e7eb";

export function invoiceTemplate(data: any) {
  return {
    pageSize: "A4",
    pageMargins: [40, 48, 40, 64],
    defaultStyle: { fontSize: 9.5, color: "#1f2937", lineHeight: 1.25 },

    content: [
      header(data),
      { canvas: [{ type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 2, lineColor: ACCENT }], margin: [0, 14, 0, 18] },
      parties(data),
      notes(data),
      itemsTable(data),
      totals(data),
    ],

    footer: (page: number, pages: number) => ({
      margin: [40, 0, 40, 0],
      columns: [
        { text: footerText(data), color: MUTED, fontSize: 8 },
        { text: `${page} / ${pages}`, alignment: "right", color: MUTED, fontSize: 8 },
      ],
    }),
  };
}

function header(d: any) {
  const c = d.company || {};
  return {
    columns: [
      {
        width: "*",
        stack: [
          { text: c.name || "", fontSize: 17, bold: true, color: ACCENT },
          { text: c.address || "", color: MUTED, margin: [0, 3, 0, 0] },
          { text: `${c.zip || ""} ${c.city || ""}`.trim(), color: MUTED },
        ],
      },
      {
        width: "auto",
        alignment: "right",
        stack: [
          { text: "FATTURA", fontSize: 20, bold: true, characterSpacing: 2 },
          { text: `N. ${invoiceNumber(d)}`, fontSize: 11, bold: true, margin: [0, 4, 0, 0] },
          { text: `Data ${formatDate(d.date)}`, color: MUTED },
          d.paid
            ? { text: "PAGATA", bold: true, color: ACCENT, fontSize: 9, margin: [0, 6, 0, 0], characterSpacing: 1 }
            : { text: "DA PAGARE", bold: true, color: "#b45309", fontSize: 9, margin: [0, 6, 0, 0], characterSpacing: 1 },
        ],
      },
    ],
  };
}

function parties(d: any) {
  const c = d.company || {};
  const k = d.client || {};
  const lines = (items: (string | undefined)[]) => items.filter((t) => t && String(t).trim()).map((t) => ({ text: String(t) }));

  return {
    columns: [
      {
        width: "*",
        stack: [
          label("Fatturato a"),
          { text: `${k.name || ""} ${k.surname || ""}`.trim(), bold: true, fontSize: 11, margin: [0, 2, 0, 2] },
          ...lines([
            k.address,
            `${k.zip || ""} ${k.city || ""}`.trim(),
            k.email,
            k.phone,
            k.fiscalcode ? `Cod. fiscale ${k.fiscalcode}` : "",
            k.fiscalnumber ? `P. IVA ${k.fiscalnumber}` : "",
          ]),
        ],
      },
      {
        width: "*",
        stack: [
          label("Emittente"),
          { text: c.name || "", bold: true, fontSize: 11, margin: [0, 2, 0, 2] },
          ...lines([
            c.fiscalcode ? `Cod. fiscale ${c.fiscalcode}` : "",
            c.fiscalnumber ? `P. IVA ${c.fiscalnumber}` : "",
            c.email,
            c.phone,
          ]),
        ],
      },
    ],
    columnGap: 30,
    margin: [0, 0, 0, 18],
  };
}

function notes(d: any) {
  const text = stripHtml(d.description);
  if (!text) return { text: "", margin: [0, 0, 0, 0] };
  return {
    margin: [0, 0, 0, 14],
    table: { widths: ["*"], body: [[{ text, color: "#334155" }]] },
    layout: {
      fillColor: () => ACCENT_SOFT,
      hLineWidth: () => 0,
      vLineWidth: (i: number) => (i === 0 ? 3 : 0),
      vLineColor: () => ACCENT,
      paddingLeft: () => 10,
      paddingRight: () => 10,
      paddingTop: () => 8,
      paddingBottom: () => 8,
    },
  };
}

function itemsTable(d: any) {
  const head = (text: string, alignment: string = "left") => ({
    text,
    bold: true,
    color: "#ffffff",
    fontSize: 8.5,
    alignment,
    characterSpacing: 0.5,
  });

  const body: any[] = [[head("#"), head("DESCRIZIONE"), head("QTÀ", "right"), head("PREZZO", "right"), head("IMPORTO", "right")]];

  (d.skills || []).forEach((row: any, i: number) => {
    body.push([
      { text: String(i + 1), color: MUTED },
      {
        stack: [
          { text: row.title || "-", bold: true },
          ...(row.details ? [{ text: row.details, color: MUTED, fontSize: 8.5, margin: [0, 2, 0, 0] }] : []),
        ],
      },
      { text: String(row.qty ?? 1), alignment: "right" },
      { text: money(row.unit ?? row.price), alignment: "right" },
      { text: money(row.price), alignment: "right", bold: true },
    ]);
  });

  if (!(d.skills || []).length) {
    body.push([{ text: "Nessuna voce", colSpan: 5, alignment: "center", color: MUTED }, {}, {}, {}, {}]);
  }

  return {
    table: { headerRows: 1, widths: [22, "*", 40, 70, 80], body },
    layout: {
      fillColor: (row: number) => (row === 0 ? ACCENT : row % 2 === 0 ? "#f8fafc" : null),
      hLineWidth: (i: number, node: any) => (i === 0 || i === node.table.body.length ? 0 : 0.5),
      hLineColor: () => LINE,
      vLineWidth: () => 0,
      paddingTop: () => 7,
      paddingBottom: () => 7,
      paddingLeft: () => 8,
      paddingRight: () => 8,
    },
  };
}

function totals(d: any) {
  const row = (name: string, value: string, big = false) => [
    { text: name, color: big ? "#ffffff" : MUTED, bold: big, fontSize: big ? 11 : 9.5 },
    { text: value, alignment: "right", color: big ? "#ffffff" : "#1f2937", bold: true, fontSize: big ? 12 : 9.5 },
  ];

  const body: any[] = [row("Imponibile", money(d.subtotal))];
  if (Number(d.vat) > 0) {
    body.push(row(`IVA ${d.company?.fiscaltype || 0}%`, money(d.vat)));
  }
  body.push(row("Totale", money(d.total), true));

  return {
    margin: [0, 16, 0, 0],
    columns: [
      { width: "*", text: "" },
      {
        width: 220,
        table: { widths: ["*", 90], body },
        layout: {
          fillColor: (i: number, node: any) => (i === node.table.body.length - 1 ? ACCENT : null),
          hLineWidth: (i: number, node: any) => (i === node.table.body.length - 1 || i === node.table.body.length ? 0 : 0.5),
          hLineColor: () => LINE,
          vLineWidth: () => 0,
          paddingTop: () => 6,
          paddingBottom: () => 6,
          paddingLeft: () => 10,
          paddingRight: () => 10,
        },
      },
    ],
  };
}

function label(text: string) {
  return { text: text.toUpperCase(), fontSize: 8, bold: true, color: ACCENT, characterSpacing: 1 };
}

function footerText(d: any): string {
  const c = d.company || {};
  return [c.name, c.fiscalnumber ? `P. IVA ${c.fiscalnumber}` : "", c.email].filter(Boolean).join("  ·  ");
}

function invoiceNumber(d: any): string {
  const year = d.date ? new Date(d.date).getFullYear() : "";
  return year ? `${d.number}/${year}` : String(d.number ?? "");
}

function stripHtml(v: any): string {
  return String(v ?? "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();
}

function formatDate(v: string): string {
  if (!v) return "";
  const d = new Date(v);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
}

function money(v: number): string {
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(v) || 0);
}
